import {
    createSite,
    deleteSite,
    getConversationStatistics,
    getSiteBySiteAddress,
    getSiteCurrentPlans,
    getSiteFeatures,
    getSitePlans,
    getSites,
    getStorageDetail,
    updateSite,
} from "../../api/site";
import { getDomainSite, setDomainSite } from "../../utils/site";
import {
    applyCouponCode,
    createPaymentMethod,
    extraSubscription,
    getBillingDetail,
    runningInvoice,
    subscribePlan,
    subscribePlanBySiteId,
    unsubscribePlan,
    unsubscribePlanBySiteId,
    updateBilling,
    updateCardInfo,
} from "../../api/billing";

import _ from "lodash";
import moment from "moment";
import { sendSocketMessage } from "../../utils/socketStream";
const recaptchaBypassSiteIds = ["6a04493101712e533c6f2d5d"];
const getRecaptchaSiteId = (siteData) => {
    if (!siteData) return null;
    if (typeof siteData === "string") {
        try {
            return JSON.parse(siteData)._id;
        } catch (e) {
            return null;
        }
    }
    return siteData._id;
};
const site = {
    namespaced: true,
    state: {
        site: getDomainSite(),
        sites: JSON.parse(localStorage.getItem("sites")) || {
            total: 0,
            count: 0,
            data: [],
        },
        billingDetail: null,
        currentPlans: [],
        currentPlanInitialized: false,
        plans: [],
        plansAvailable: false,
        features: {},
        initializedFeatures: false,
        conversationStatistics: [],
        storage: {
            percentage: 0,
            maxDiskSpace: 0,
            exceededSpace: 0,
            perGBPrice: 0,
        },
        loadingSubscribe: false,
    },
    getters: {
        getSite: (state) => (id) => {
            return state.sites.data.find((u) => u._id === id);
        },
        isTrialMode: (state, getters, rootState) => {
            const settings = rootState.setting.settings;
            const trialDays = _.get(settings, "plan.trialDays", 0);
            const trialExpire = moment(state.site.createdAt).add(trialDays, "days");
            const diff = trialExpire.diff(moment(), "milliseconds");
            return diff > 0;
        },
        isSuspend: (state) => {
            return _.get(state.site, "suspend", false);
        },
        isSavedCreditCard: (state) => {
            return !!(_.get(state.billingDetail, "iyzicoCardToken") && _.get(state.billingDetail, "iyzicoCardUserKey"));
        },
        isRecaptchaBypassed: (state) => {
            return recaptchaBypassSiteIds.indexOf(getRecaptchaSiteId(state.site)) !== -1;
        },
        isCreditCardCustomer: (state) => {
            return _.get(state.site, "paymentMethod", "credit-card") === "credit-card";
        },
        paidSubscriptions: (state) => {
            const mappedCurrentPlans = state.currentPlans.reduce((acc, plan) => {
                acc[plan.planId] = plan;
                return acc;
            }, {});
            const mappedPlans = state.plans.reduce((acc, plan) => {
                acc[plan._id] = plan;
                return acc;
            }, {});
            return Object.keys(mappedCurrentPlans).filter((key) => mappedPlans[key]).filter((key) => {
                const currentPlanAction = mappedCurrentPlans[key];
                const currentPlan = mappedPlans[key];
                const tiers = currentPlan.selectFeatures || [];
                const selectedTierCount = _.get(currentPlanAction, "details.maxUsageCount", 0);
                const selectedTier = tiers.find((tier) => tier.count === selectedTierCount);
                const planPrice = selectedTier ? selectedTier.price : currentPlan.price;
                return planPrice > 0;
            });
        },
    },
    mutations: {
        SET_SITES: (state, payload) => {
            localStorage.setItem("sites", JSON.stringify(payload));
            state.sites = payload;
        },
        ADD_SITE: (state, payload) => {
            state.sites.data.push(payload);
            state.sites.total++;
        },
        UPDATE_SITE: (state, payload) => {
            let adminUser = state.sites.data.findIndex((p) => p._id === payload._id);
            state.sites.data[adminUser] = payload;
        },
        GET_SITE_BY_SITE_ADDRESS: (state, payload) => {
            state.site = payload;
        },
        REMOVE_SITE: (state, id) => {
            state.sites.data = state.sites.data.filter((u) => u._id !== id);
            state.sites.total--;
        },
        SET_CURRENT_PLANS: (state, currentPlans) => {
            state.currentPlans = currentPlans;
            state.currentPlanInitialized = true;
        },
        SET_BILLING_DETAIL: (state, billingDetail) => {
            state.billingDetail = billingDetail;
        },
        SET_SITE_PLANS: (state, plans) => {
            state.plans = plans;
        },
        SET_PLANS_AVAILABLE: (state, val) => {
            state.plansAvailable = val;
        },
        SET_SITE_FEATURES: (state, features) => {
            state.features = features;
            state.initializedFeatures = true;
        },
        SET_SITE_CONVERSATION_STATISTICS: (state, conversationStatistics) => {
            state.conversationStatistics = conversationStatistics;
        },
        UPDATE_SITE_DATA: (state, data) => {
            state.site = data;
        },
        SET_STORAGE_DETAIL: (state, storage) => {
            state.storage = storage;
        },
        CHANGE_SUSPEND_STATUS: (state, status) => {
            state.site.suspend = status;
        },
        SET_LOADING_SUBSCRIBE: (state, loading) => {
            state.loadingSubscribe = loading;
        },
    },
    actions: {
        getSites({ commit }, payload) {
            return getSites(payload.perPage, payload.page, payload.extendedMeta, payload.cache).then((response) => {
                commit("SET_SITES", response.data);
            });
        },
        getSite({ state }, id) {
            return state.sites.data.find((u) => u._id === id);
        },
        delSite({ commit }, id) {
            return deleteSite(id).then(() => {
                commit("REMOVE_SITE", id);
            });
        },
        createSite({ commit }, payload) {
            return createSite(payload).then((response) => {
                commit("ADD_SITE", response.data);
            });
        },
        updateSite({ commit }, payload) {
            return updateSite(payload._id, payload).then((response) => {
                commit("UPDATE_SITE", response.data);
            });
        },
        getSiteBySiteAddress({ commit }, payload) {
            return getSiteBySiteAddress(payload).then((response) => {
                commit("GET_SITE_BY_SITE_ADDRESS", response.data);
                setDomainSite(JSON.stringify(_.pick(response.data, ['_id', 'alias', 'clientAddress', 'customerClientAddress', 'createdAt'])));
                return response;
            });
        },
        getSiteCurrentPlans({ commit }) {
            return getSiteCurrentPlans().then((currentPlans) => {
                commit("SET_CURRENT_PLANS", currentPlans);
            });
        },
        getBillingDetail({ commit, state }) {
            return getBillingDetail().then((res) => {
                const billingDetail = res.data;
                commit("SET_BILLING_DETAIL", billingDetail);

                return billingDetail;
            });
        },
        createPaymentMethod({ commit, dispatch }, payload) {
            return createPaymentMethod(payload).then(async (res) => {
                const billingDetail = res.data;
                commit("SET_BILLING_DETAIL", billingDetail);
                await dispatch("getSitePlans");
                await dispatch("getSiteCurrentPlans");
                await dispatch("getSiteFeatures");
                await dispatch("setting/getSettings", null, { root: true });
                return res.data;
            });
        },
        updateCardInfo({ commit, dispatch }, payload) {
            return updateCardInfo(payload).then(async (res) => {
                const billingDetail = res.data;
                if (billingDetail.status !== "error") {
                    commit("SET_BILLING_DETAIL", billingDetail);
                    await dispatch("getSitePlans");
                    await dispatch("getSiteCurrentPlans");
                    await dispatch("getSiteFeatures");
                    await dispatch("setting/getSettings", null, { root: true });
                }
                return billingDetail;
            });
        },
        updateBilling({ commit, state }, payload) {
            return updateBilling(payload).then((res) => {
                const billingDetail = res.data;
                commit("SET_BILLING_DETAIL", billingDetail);
                return billingDetail;
            });
        },
        getSitePlans({ commit }) {
            return getSitePlans().then((data) => {
                commit("SET_SITE_PLANS", data);
                commit("SET_PLANS_AVAILABLE", true);
                return data;
            });
        },
        subscribePlan({ commit, state, dispatch }, { siteId, planId, period, details, couponCode }) {
            const promise = siteId
                ? subscribePlanBySiteId(siteId, planId, period, details)
                : subscribePlan(planId, period, details, couponCode);
            commit("SET_LOADING_SUBSCRIBE", true);
            return promise.then((planAction) => {
                if (planAction && planAction.status !== "error") {
                    const filtered = state.currentPlans.filter((p) => {
                        if (p.planType !== planAction.planType) return true;
                        if (planAction.period === "monthly") return false;
                        if (p.period === "monthly") return false;
                        if (p.planId === planAction.planId && p.period === planAction.period) return false;
                        return true;
                    });
                    commit("SET_CURRENT_PLANS", [...filtered, planAction]);
                    dispatch("getSiteFeatures");
                    sendSocketMessage("user subscription-changed", { siteId: state.site._id });
                }
                return planAction;
            }).finally(() => {
                commit("SET_LOADING_SUBSCRIBE", false);
            });
        },
        unsubscribePlan({ commit, state, dispatch }, { siteId, planId, immediateCancel }) {
            const promise = siteId
                ? unsubscribePlanBySiteId(siteId, planId, immediateCancel)
                : unsubscribePlan(planId, immediateCancel);
            return promise.then(async (res) => {
                await dispatch("getSiteCurrentPlans");
                dispatch("getSiteFeatures");
                sendSocketMessage("user subscription-changed", { siteId: state.site._id });
                return res;
            });
        },
        extraSubscription({ commit, state, dispatch }, { planType, subscription }) {
            return extraSubscription(planType, subscription).then((plan) => {
                if(plan.status === "error") {
                    return plan;
                }
                commit("SET_SITE_FEATURES", {
                    ...state.features,
                    totalSessionCount: (state.features.totalSessionCount || 0) + subscription.sessionCount,
                });
                dispatch("getSiteFeatures");
            });
        },
        getSiteFeatures({ commit, state }, siteId) {
            return getSiteFeatures(siteId).then((features) => {
                commit("SET_SITE_FEATURES", features);
                return features;
            });
        },
        getConversationStatistics({ commit }) {
            return getConversationStatistics()
                .then((res) => res.data)
                .then((statistics) => {
                    commit("SET_SITE_CONVERSATION_STATISTICS", statistics);
                });
        },
        getStorageDetail({ commit }) {
            return getStorageDetail().then((storageDetail) => {
                commit("SET_STORAGE_DETAIL", storageDetail);
            });
        },
        getRunningInvoice({}, onlyPrice) {
            return runningInvoice(onlyPrice);
        },
        applyCouponCode({ commit, state, dispatch }, couponCode) {
            return applyCouponCode(couponCode).then((data) => {
                commit("UPDATE_SITE_DATA", {
                    ...state.site,
                    partner: {
                        name: data.partnerName,
                    },
                });
                dispatch("getSiteFeatures");
                return data;
            });
        },
        updateSiteData({ commit, state }, payload) {
            commit("UPDATE_SITE_DATA", {
                ...state.site,
                ...payload,
            });
        },
        /*deleteAdminUser({commit}, id) {
          commit('REMOVE_ADMIN_USER', id);
          return deleteAdminUser(id);
        }*/
    },
};

export default site;
